import React from "react";
import { Flex, type FlexProps, type IconProps } from "@chakra-ui/react";
import { Soc2Badge } from "./soc-2-badge";
import { GdprBadge } from "./gdpr-badge";
import { HipaaBadge } from "./hipaa-badge";
import { CcpaBadge } from "./ccpa-badge";
import { IsoBadge } from "./iso-badge";

const sharedProps = {
  flex: 1,
  flexShrink: { base: 0, md: 1 },
  // XX: Using `#3A3A3C` instead of a brand color so all badges match ISO, which has strict rules against any altering
  foregroundColor: "#3A3A3C",
};

export type SecurityBadgesProps = {
  /**
   * Whether to show the HIPAA badge.
   * @default true
   */
  hasHipaa?: boolean;

  /**
   * The background color to use on the badge.
   * @default "new.gray.50"
   */
  bgColor?: "white" | "new.gray.50";

  /**
   * The size of the badge.
   * @default '{ base: "72px", sm: "84px", md: "96px", lg: "120px" }'
   */
  sizes?: IconProps["boxSize"];

  /**
   * The gap between the badges.
   * @default '{ base: "small", md: "bigger" }'
   */
  gap?: FlexProps["gap"];
} & Omit<FlexProps, "gap">;

export function SecurityBadges({
  hasHipaa = true,
  bgColor = "new.gray.50",
  sizes = { base: "72px", sm: "84px", md: "96px", lg: "120px" },
  gap = { base: "small", md: "bigger" },
  ...flexProps
}: SecurityBadgesProps) {
  const badgeProps = { backgroundColor: bgColor, boxSize: sizes, minW: sizes, maxW: sizes, ...sharedProps };

  return (
    <Flex mx={{ md: "auto" }} gap={gap} flexWrap={{ base: "wrap", md: "nowrap" }} {...flexProps}>
      <Soc2Badge {...badgeProps} />
      <GdprBadge {...badgeProps} />
      {hasHipaa && <HipaaBadge {...badgeProps} />}
      <CcpaBadge accentColor="new.gray.150" {...badgeProps} />
      <IsoBadge {...badgeProps} />
    </Flex>
  );
}
